/*
 created by: Leo
 purpose: determines which selection of products can be bought together when a product is pick up in store only
*/

/*
  CartStoreHandler
  ----------------
  Manages conflict resolution when a cart contains a mix of deliverable
  and in-store-only products.

  Expects a JSON array of cart items serialised from Liquid:
  [
    {
      "productId": "1234567890",
      "isGift": false,
      "availability": "In Store Only" | "Available Now" | "Coming Soon"
                    | "Back Order" | "Pre Order" | "Out Of Stock" | null,
      "locationStock": [
        { "key": "online",     "label": "Online",     "available": true },
        { "key": "canal_walk", "label": "Canal Walk", "available": true },
        { "key": "gateway",    "label": "Gateway",    "available": false }
      ]
    },
    ...
  ]

  ⚠️ Only "In Store Only" is ever tested, and always as a NEGATION for
  "deliverable" — never `=== "available"`, which is not one of the six values
  this metafield can hold. (This comment used to say "available"; it was wrong.)
  The values are written by the lego-content app, ShopifyMetafieldBuilder.

  The "available" boolean indicates whether the location can fulfill the
  current cart quantity (computed server-side: count >= item.quantity).
  The online/delivery channel is identified either by the key "shop_location"
  or by the label "Online"; all other entries are physical store locations.

  "isGift" marks a gift-with-purchase line (the GWP product tag). Gifts are NOT
  excluded from the conflict check — they go red like anything else — but they
  cannot be removed the same way: deleting the line does nothing, because the
  gift engine re-awards it on the next cart evaluation. The conflict card offers
  a button that asks the engine to drop them instead, per store. See
  _offerGiftRemoval() below.
*/

const CART_ONLINE_STORE_KEY = "shop_location";
// Live metafield data labels the delivery channel "Online" but uses a key other
// than "shop_location", so we also match on the label to keep it out of the
// physical-store dropdown.
const CART_ONLINE_STORE_LABEL = "online";

class CartStoreHandler {
  constructor(cartItemsArray) {
    this._parseCartData(cartItemsArray);
    this._queryDomElements();

    // Track user's current selection so we can restore it after cart updates
    this.currentSelection = null; // 'online' | storeKey | null
  }

  // ─── Data & DOM Refresh ─────────────────────────────────────────────────────

  _parseCartData(cartItemsArray) {
    // Build a product-keyed map, deduplicating by product ID.
    // availability and locationStock are product-level so duplicates are identical.
    this.data = {};
    cartItemsArray.forEach((item) => {
      this.data[item.productId] = {
        title: item.title || null,
        isGift: item.isGift === true,
        availability: item.availability || null,
        locationStock: item.locationStock || [],
      };
    });

    this._rememberStores();
  }

  /**
   * Accumulates every store seen in the cart, and never forgets one.
   *
   * ⚠️ The pick-up dropdown used to be rebuilt from whatever is in the cart
   * RIGHT NOW, which made it collapse when an item was removed: a product whose
   * `location_stock_count` metafield has never been written contributes no
   * locations at all, so if the removed item was the only one carrying the
   * metafield, the shopper was left on "Pick up in store" with an empty store
   * list and no way forward. Removing a gift did exactly that.
   *
   * Stores are a property of the retailer, not of the basket, so treat them as
   * append-only for the life of the page.
   */
  _rememberStores() {
    if (!this.knownStores) this.knownStores = new Map();

    for (const productId in this.data) {
      (this.data[productId].locationStock || []).forEach((store) => {
        if (!store?.key) return;
        if (this._isOnlineChannel(store)) return;
        if (!this.knownStores.has(store.key)) {
          this.knownStores.set(store.key, store.label);
        }
      });
    }
  }

  _queryDomElements() {
    this.form = document.querySelector(".cart__contents");
    this.checkoutButton = document.querySelector("#checkout");
    // Step 1: how would you like to receive your order? ('' | 'online' | 'pickup')
    this.fulfilmentSelect = document.querySelector("#fulfilment-select");
    // Step 2 panels, revealed based on the step-1 choice
    this.deliveryPanel = document.querySelector("#delivery-panel");
    this.pickupPanel = document.querySelector("#pickup-panel");
    this.onlineButton = document.querySelector("#online_button");
    this.storeSelect = document.querySelector("#store-select");
    this.pickupConfirmButton = document.querySelector("#pickup_confirm_button");
    this.conflictMessage = document.getElementById("conflict-message");
    this.warningBanner = document.querySelector(".instore-warning-banner");
  }

  /**
   * Re-reads cart data from the DOM and re-queries elements.
   * Called after Shopify's cart AJAX updates the page.
   */
  /**
   * Re-initialise when something OTHER than us replaces the cart markup.
   *
   * ⚠️ We are not the only thing that re-renders this cart. Dawn's own
   * CartItems.onCartUpdate() replaces the entire <cart-items> innerHTML from a
   * fetch, and anything else added later could too. When that lands after our
   * work it silently undoes it — the shopper's chosen store flashed back in and
   * then reset itself, with no error anywhere to explain it.
   *
   * Dawn's duplicate re-render is now suppressed for the gift app's own sources
   * (assets/cart.js), but this watcher is the general guard: whatever wipes the
   * markup, the picker comes back.
   */
  _watchForExternalRerender() {
    const container = document.querySelector("cart-items");
    if (!container || typeof MutationObserver !== "function") return;

    const observer = new MutationObserver(() => {
      // Only a genuinely NEW #cart-items-data means the markup was replaced.
      // Our own DOM writes (building <option>s, drawing the conflict card)
      // leave that element alone, so this cannot feed itself.
      if (document.getElementById("cart-items-data") === this._lastCartDataEl) {
        return;
      }

      clearTimeout(this._externalRerenderTimer);
      this._externalRerenderTimer = setTimeout(() => {
        const savedSelection = this.currentSelection;
        this._refreshFromDom();
        this._reinitialize(savedSelection);
      }, 0);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  _refreshFromDom() {
    const cartDataEl = document.getElementById("cart-items-data");
    // Marks which render we are looking at, for _watchForExternalRerender().
    this._lastCartDataEl = cartDataEl;
    if (!cartDataEl) {
      // Cart is now empty, nothing to do
      this.data = {};
      this._queryDomElements();
      return;
    }

    try {
      const cartData = JSON.parse(cartDataEl.textContent);
      this._parseCartData(cartData);
    } catch (e) {
      // Keep the previous data — stale beats none — but DO NOT return.
      console.error(
        "CartStoreHandler: failed to parse cart data on refresh",
        e,
      );
    }

    // ⚠️ ALWAYS re-query, including after a parse failure. Shopify's section
    // swap replaces every element this class holds a reference to, so skipping
    // this leaves the handler bound to detached nodes: the dropdowns on screen
    // are new and have no listeners, _buildStoreDropdown() fills a <select>
    // nobody can see, and the shopper is left with a picker that does nothing.
    // Silent, and indistinguishable from "the JS never ran".
    this._queryDomElements();
  }

  /**
   * Subscribes to Shopify's cart-update pub/sub event.
   * When cart changes, refreshes data and re-initializes.
   */
  _subscribeToCartUpdates() {
    // Check if Shopify's pub/sub system is available
    if (
      typeof subscribe !== "function" ||
      typeof PUB_SUB_EVENTS === "undefined"
    ) {
      console.warn(
        "CartStoreHandler: pub/sub not available, cart updates won't be tracked",
      );
      return;
    }

    // console.log(subscribe);

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // ⚠️ Skip "gift-app". That event is published by the engine the moment it
      // finishes WRITING to the cart — before the re-render block has fetched
      // and swapped the new section HTML. Acting on it meant parsing the
      // PRE-swap #cart-items-data, i.e. a cart that still contained the gift
      // that had just been removed, and then doing the whole job again a beat
      // later on "gift-app-rerender". Wait for the swap.
      if (event?.source === "gift-app") return;

      // Save current selection before refresh
      const savedSelection = this.currentSelection;

      // Wait a tick for DOM to be fully updated by Shopify's handlers
      setTimeout(() => {
        this._refreshFromDom();
        this._reinitialize(savedSelection);
      }, 0);
    });
  }

  /**
   * Re-runs initialization logic after a cart update.
   * Attempts to restore the user's previous selection if still valid.
   */
  _reinitialize(savedSelection) {
    // Fall back to the store the gift engine has remembered. currentSelection
    // is in-memory and can be lost — most visibly right after removing a gift,
    // where the shopper watched their choice flash back to "Please Select" and
    // had to reload the page to get it back. The engine persists the store for
    // its own reasons (a dropped gift stays dropped only while that store is
    // chosen), so it is the authority whenever ours is missing.
    savedSelection =
      savedSelection || window.giftApp?.getCollectionStore?.() || null;

    if (!this.hasConflict()) {
      this._enableCheckout();
      this._clearConflictMessage();
      this.highlightProducts([]);
      return;
    }

    this._disableCheckout();
    this._buildStoreDropdown();
    this._configureMethodOptions();
    this._bindEvents();

    // Restore previous selection if we had one, otherwise start fresh
    if (savedSelection) {
      this._restoreSelection(savedSelection);
    } else {
      this._resetSelection();
    }
  }

  /**
   * Restores the user's previous selection after cart re-render.
   * @param {'online' | string} selection - 'online' or a store key
   */
  _restoreSelection(selection) {
    if (selection === "online") {
      if (this.fulfilmentSelect) this.fulfilmentSelect.value = "online";
      this._showPanel("online");
      this._applyOnline();
      return;
    }

    // It's a store key - reveal the pickup panel and re-select if still valid
    if (this.fulfilmentSelect) this.fulfilmentSelect.value = "pickup";
    this._showPanel("pickup");

    if (!this.storeSelect) return;
    const optionExists = Array.from(this.storeSelect.options).some(
      (opt) => opt.value === selection,
    );
    if (optionExists) {
      this.storeSelect.value = selection;
      const storeName =
        this.storeSelect.options[this.storeSelect.selectedIndex].text;
      this._applyStore(selection, storeName);
    } else {
      // Store no longer valid, reset the pickup step to pending
      this.storeSelect.value = "";
      this._hidePickupConfirm();
      this.highlightProducts([]);
      this._disableCheckout();
      this._clearConflictMessage();
      this._setBannerStatus("pending");
      this.currentSelection = null;
    }
  }

  // ─── Public ──────────────────────────────────────────────────────────────

  init() {
    // Subscribe to cart updates so we can re-initialize after AJAX changes
    this._subscribeToCartUpdates();
    // …and catch re-renders that arrive without one.
    this._lastCartDataEl = document.getElementById("cart-items-data");
    this._watchForExternalRerender();
    // console.log("----------- subscribing ------------");

    if (!this.hasConflict()) {
      this._enableCheckout();
      return;
    }

    this._disableCheckout();
    this._buildStoreDropdown();
    this._configureMethodOptions();
    this._bindEvents();

    // The shopper's store survives a page load — the gift engine remembers it,
    // because a gift they dropped stays dropped only while that store is the
    // chosen one. Ask the engine rather than reading its storage: the key name
    // and mechanism must live in one repo (this has silently broken twice).
    const savedStore = window.giftApp?.getCollectionStore?.();
    if (savedStore) {
      this._restoreSelection(savedStore);
    } else {
      this._resetSelection();
    }
  }

  /**
   * A conflict exists when the cart has more than one unique product
   * and at least one of them is marked "In Store Only".
   */
  hasConflict() {
    const productIds = Object.keys(this.data);
    if (productIds.length <= 1) return false;
    return productIds.some(
      (id) => this.data[id].availability === "In Store Only",
    );
  }

  /**
   * Returns all physical store locations found across every cart item,
   * excluding the online/delivery channel (see _isOnlineChannel).
   *
   * @returns {Array<{ key: string, label: string }>}
   */
  getAllStores() {
    // From the append-only list, not from the current cart — see
    // _rememberStores() for why removing an item must not shrink this.
    // TEMPORARY: to hide a location from the pickup dropdown, filter it here.
    // e.g. .filter(([, label]) => label.trim().toLowerCase() !== "menlyn park")
    return Array.from(this.knownStores?.entries() ?? []).map(
      ([key, label]) => ({ key, label }),
    );
  }

  /**
   * Whether a locationStock entry is the online/delivery channel rather than a
   * physical store. Delivery is handled by the "Deliver my items" button, so
   * this channel must be kept out of the "Pick up in store" dropdown.
   * @param {{ key?: string, label?: string }} store
   * @returns {boolean}
   */
  _isOnlineChannel(store) {
    if (!store) return false;
    if (store.key === CART_ONLINE_STORE_KEY) return true;
    return (store.label || "").trim().toLowerCase() === CART_ONLINE_STORE_LABEL;
  }

  /**
   * Returns an array of product IDs that cannot be fulfilled by the
   * given selection.
   *
   * @param {string} selection - 'online' or a store key (e.g. 'canal_walk')
   * @returns {string[]}
   */
  getProductsToHighlight(selection) {
    const toHighlight = [];
    // console.log(this.data);
    for (const productId in this.data) {
      const product = this.data[productId];

      if (selection === "online") {
        // In-store-only items can't be delivered
        if (product.availability === "In Store Only") {
          toHighlight.push(productId);
        }
      } else {
        // Items with no stock (or missing entry) at the chosen store
        const storeEntry = (product.locationStock || []).find(
          (s) => s.key === selection,
        );
        // console.log("store entry = ", storeEntry);
        if (!storeEntry || !storeEntry.available) {
          toHighlight.push(productId);
        }
      }

      /*
      console.log(
        "checking products to highlight. product id: ",
        productId,
        " --- to highlight: ",
        toHighlight,

      );
      */
    }
    return toHighlight;
  }

  /**
   * Applies .cart-item--conflict to DOM elements for the given product IDs,
   * clears it from all others, then updates the checkout button state.
   *
   * @param {string[]} productIds
   */
  highlightProducts(productIds) {
    // Clear existing highlights
    document.querySelectorAll("[data-product-id]").forEach((el) => {
      el.classList.remove("cart-item--conflict");
    });

    // Apply new highlights
    productIds.forEach((productId) => {
      document
        .querySelectorAll(`[data-product-id="${productId}"]`)
        .forEach((el) => {
          el.classList.add("cart-item--conflict");
        });
    });

    this._updateCheckoutState(productIds.length === 0);
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  _buildStoreDropdown() {
    if (!this.storeSelect) return;

    // Drop any options we added last time before re-adding. _reinitialize()
    // calls this on every cart update, and when Shopify's section swap has NOT
    // replaced the <select> the options would otherwise pile up.
    Array.from(this.storeSelect.options).forEach((option) => {
      if (option.value !== "") option.remove();
    });

    this.getAllStores().forEach((store) => {
      const option = document.createElement("option");
      option.value = store.key;
      option.textContent = store.label;
      this.storeSelect.appendChild(option);
    });
  }

  _bindEvents() {
    // Step 1: choose delivery vs pickup
    this.fulfilmentSelect?.addEventListener("change", (e) => {
      this._selectMethod(e.target.value);
    });

    // Step 2 (pickup): choosing a store applies the selection immediately
    this.storeSelect?.addEventListener("change", (e) => {
      const storeKey = e.target.value;
      if (!storeKey) {
        // User reset the dropdown back to the placeholder
        this._hidePickupConfirm();
        this.highlightProducts([]);
        this._disableCheckout();
        this._clearConflictMessage();
        this._setBannerStatus("pending");
        this.currentSelection = null;
        return;
      }
      const storeName = e.target.options[e.target.selectedIndex].text;
      this._applyStore(storeKey, storeName);
    });

    // Both CTAs act as the final "proceed" affordance
    this.onlineButton?.addEventListener("click", (e) => {
      e.preventDefault();
      this._handleCtaClick();
    });
    this.pickupConfirmButton?.addEventListener("click", (e) => {
      e.preventDefault();
      this._handleCtaClick();
    });
  }

  /**
   * Handles the step-1 method choice: reveals the matching panel. Delivery is
   * applied immediately (no further input needed); pickup waits for a store.
   * @param {'' | 'online' | 'pickup'} method
   */
  _selectMethod(method) {
    // Any method change clears a pending store selection
    if (this.storeSelect) this.storeSelect.value = "";
    this._hidePickupConfirm();

    if (method === "online") {
      this._showPanel("online");
      this._applyOnline();
      return;
    }

    this._showPanel(method === "pickup" ? "pickup" : null);

    // Pickup with no store yet, or the "- Please Select -" placeholder: pending
    this.highlightProducts([]);
    this._disableCheckout();
    this._clearConflictMessage();
    this._setBannerStatus("pending");
    this.currentSelection = null;
  }

  /**
   * Applies the delivery choice: highlights the pick-up-only items that must be
   * removed and updates the checkout/banner state.
   */
  _applyOnline() {
    const toHighlight = this.getProductsToHighlight("online");
    this.highlightProducts(toHighlight);
    this._setConflictMessage(toHighlight, "online");
    this._setBannerStatus(toHighlight.length === 0 ? "resolved" : "conflict");
    this.currentSelection = "online";
    this._notifyGiftEngine(null);
  }

  /**
   * Tells the gift engine which store the shopper is collecting from, so gifts
   * they dropped at that store stay dropped and gifts dropped at another store
   * come back. Delivery passes null — that path never checks stock.
   *
   * Safe to call on every re-render: the engine ignores a store it is already
   * on, which is what stops this and the engine's own cart writes looping.
   *
   * @param {string|null} storeKey
   */
  _notifyGiftEngine(storeKey) {
    window.giftApp?.setCollectionStore?.(storeKey);
  }

  /**
   * Applies a pickup-store choice: highlights items unavailable at that store,
   * updates the checkout/banner state and reveals the confirm CTA.
   * @param {string} storeKey
   * @param {string} storeName
   */
  _applyStore(storeKey, storeName) {
    const toHighlight = this.getProductsToHighlight(storeKey);
    this.highlightProducts(toHighlight);
    this._setConflictMessage(toHighlight, "store", storeName, storeKey);
    this._setBannerStatus(toHighlight.length === 0 ? "resolved" : "conflict");
    this._showPickupConfirm(storeName);
    this.currentSelection = storeKey;
    this._notifyGiftEngine(storeKey);
  }

  _setConflictMessage(conflictingIds, mode, storeName, storeKey) {
    if (!this.conflictMessage) return;

    // Clear existing content
    while (this.conflictMessage.firstChild) {
      this.conflictMessage.removeChild(this.conflictMessage.firstChild);
    }

    // text-body-lg matches the "Action required!" heading size
    const title = document.createElement("div");
    title.className = "conflict-card-title text-body-lg";
    const desc = document.createElement("div");
    desc.className = "conflict-card-desc";

    // Resolved: green success card
    if (conflictingIds.length === 0) {
      this.conflictMessage.dataset.state = "success";
      this.conflictMessage.hidden = false;
      title.textContent = "All items can be fulfilled";
      desc.textContent = "You may proceed to checkout.";
      this.conflictMessage.append(title, desc);
      return;
    }

    // Conflict: red error card — title, description and the list of items to remove
    this.conflictMessage.dataset.state = "error";
    this.conflictMessage.hidden = false;
    title.textContent =
      mode === "online" ? "To continue with delivery" : `To pick up at ${storeName}`;

    // Gifts are split off from the shopper's own items. Telling someone to
    // "remove" a gift is a dead end — deleting the line does nothing, because
    // the gift engine re-awards it on the next evaluation. They get a button
    // that asks the engine to drop it instead.
    //
    // ⚠️ This used to be pickup-only, on the reasoning that the engine keys a
    // dropped gift to a store and delivery has no store. The gift then fell into
    // the "please remove" list on the delivery path — a line with no remove
    // control, which the engine put straight back. That was the dead end the
    // 2026-09-03 reports were about. The engine gained a delivery bucket on
    // 2026-09-04, so both paths now offer the button.
    //
    // ⚠️ Only offer the button when there is an engine to honour it. With the
    // app embed switched off, window.giftApp is undefined and the button would
    // close the dialog and do nothing at all. Gifts already sitting in carts
    // outlive the embed being turned off — Shopify carts last about two weeks —
    // so this is a real state, not a theoretical one. Listing them with the
    // shopper's own items is still a dead end, but an honest one.
    const canDropGifts =
      typeof window.giftApp?.suppressGiftsAtStore === "function";
    const giftIds = canDropGifts
      ? conflictingIds.filter((id) => this.data[id]?.isGift)
      : [];
    const ownIds = conflictingIds.filter((id) => !giftIds.includes(id));

    this.conflictMessage.append(title);

    if (ownIds.length > 0) {
      desc.textContent =
        mode === "online"
          ? "Please remove the highlighted pick up-only item(s):"
          : "Please remove the highlighted item(s):";
      this.conflictMessage.append(desc, this._buildItemList(ownIds));
    }

    if (giftIds.length > 0) {
      this.conflictMessage.append(
        this._buildGiftPrompt(giftIds, mode, storeKey, storeName),
      );
    }
  }

  /**
   * The key handed to the engine for a drop. On the delivery path the theme has
   * no store, so it sends "online" and the engine files it under its own
   * delivery bucket — the theme never needs to know that bucket's name.
   * @param {'online' | 'store'} mode
   * @param {string} [storeKey]
   * @returns {string|undefined}
   */
  _dropKeyFor(mode, storeKey) {
    return mode === "online" ? "online" : storeKey;
  }

  /**
   * A list of cart items, each linking to its line on the page.
   * @param {string[]} productIds
   * @param {boolean} [withGiftBadge=false] - Tag each entry as a free gift
   * @returns {HTMLUListElement}
   */
  _buildItemList(productIds, withGiftBadge = false) {
    const ul = document.createElement("ul");
    ul.className = "conflict-item-list";

    productIds.forEach((productId) => {
      const productData = this.data[productId];
      const itemTitle = productData?.title || productId;
      const targetEl = document.querySelector(
        `[data-product-id="${productId}"]`,
      );

      const li = document.createElement("li");
      const a = document.createElement("a");
      a.textContent = itemTitle;
      a.href = "#";
      a.addEventListener("click", (e) => {
        e.preventDefault();
        if (targetEl)
          targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
      });

      li.appendChild(a);

      if (withGiftBadge) {
        const badge = document.createElement("span");
        badge.className = "conflict-item-badge";
        badge.textContent = "Free Gift";
        li.appendChild(badge);
      }

      ul.appendChild(li);
    });

    return ul;
  }

  /**
   * Clones the trash icon rendered once by the cart snippet. Liquid owns the
   * asset so the cart keeps a single trash icon; JS cannot inline it itself.
   * @returns {SVGElement|null}
   */
  _trashIcon() {
    const template = document.getElementById("gift-remove-icon");
    const svg = template?.content?.querySelector("svg");
    return svg ? svg.cloneNode(true) : null;
  }

  /**
   * The gift half of the conflict card: what can't be fulfilled, and the button
   * that offers to drop it. Rebuilt on every render, so the listener cannot
   * stack up — _setConflictMessage() empties the card before calling this.
   * @param {string[]} giftIds
   * @param {'online' | 'store'} mode
   * @param {string} [storeKey]
   * @param {string} [storeName]
   * @returns {HTMLDivElement}
   */
  _buildGiftPrompt(giftIds, mode, storeKey, storeName) {
    const wrap = document.createElement("div");
    wrap.className = "conflict-gift-prompt";

    const desc = document.createElement("div");
    desc.className = "conflict-card-desc";
    // "(s)" rather than a count, matching "Remove Gift(s)" in the gift modal.
    // The delivery wording never mentions a store: there isn't one, and an
    // in-store-only gift cannot be shipped from any of them.
    desc.textContent =
      mode === "online"
        ? "Your free gift(s) can't be delivered:"
        : "Your free gift(s) can't be collected from this store:";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-rounded-red text-label-md font-500";

    const icon = this._trashIcon();
    if (icon) button.appendChild(icon);
    const label = document.createElement("span");
    label.textContent = "Remove free gift(s)";
    button.appendChild(label);

    button.addEventListener("click", () =>
      this._openGiftDialog(giftIds, mode, storeKey, storeName),
    );

    wrap.append(desc, this._buildItemList(giftIds, true), button);
    return wrap;
  }

  /**
   * Confirmation before dropping gifts. Nothing here is permanent — choosing a
   * different store brings them back — so it explains rather than warns.
   * @param {string[]} giftIds
   * @param {'online' | 'store'} mode
   * @param {string} [storeKey]
   * @param {string} [storeName]
   */
  _openGiftDialog(giftIds, mode, storeKey, storeName) {
    const dropKey = this._dropKeyFor(mode, storeKey);
    const dialog = document.getElementById("gift-collect-dialog");
    if (!dialog) {
      // No dialog in the DOM — drop the gifts rather than dead-ending.
      this._dropGifts(giftIds, dropKey);
      return;
    }

    const setText = (id, text) => {
      const el = dialog.querySelector(id);
      if (el) el.textContent = text;
    };

    setText(
      "#gift-collect-title",
      "Are you sure you want to remove your free gift(s)?",
    );

    // The store name is bold, so the pickup wording is built from nodes rather
    // than set as text. textContent on the store name keeps it escaped.
    //
    // ⚠️ Delivery gets its own sentence and never touches storeName. _applyOnline()
    // calls _setConflictMessage() with no store name at all, so interpolating it
    // here would render the literal string "undefined" to the shopper.
    const intro = dialog.querySelector("#gift-collect-intro");
    if (intro) {
      if (mode === "online" || !storeName) {
        intro.replaceChildren(
          document.createTextNode(
            "Your free gift(s) can't be delivered and will be removed from your bag:",
          ),
        );
      } else {
        const store = document.createElement("strong");
        store.textContent = storeName;
        intro.replaceChildren(
          document.createTextNode("Your free gift(s) can't be collected from "),
          store,
          document.createTextNode(" and will be removed from your bag:"),
        );
      }
    }

    // No "…and they'll come back if you pick another store" line here. It was
    // only half true: the gift IS re-awarded at a different store, but if it
    // isn't stocked there either it is flagged again immediately, so the
    // promise could not be kept. The modal states what happens and stops.

    const list = dialog.querySelector("#gift-collect-list");
    if (list) {
      list.replaceChildren();
      giftIds.forEach((productId) => {
        const li = document.createElement("li");
        li.textContent = this.data[productId]?.title || productId;
        list.append(li);
      });
    }

    const confirm = dialog.querySelector("#gift-collect-confirm");
    const cancel = dialog.querySelector("#gift-collect-cancel");
    if (confirm) {
      const confirmLabel = document.createElement("span");
      confirmLabel.textContent = "Remove free gift(s)";
      const confirmIcon = this._trashIcon();
      confirm.replaceChildren(
        ...(confirmIcon ? [confirmIcon, confirmLabel] : [confirmLabel]),
      );
      // onclick, not addEventListener — the dialog is a long-lived element, so
      // listeners would stack every time it is opened.
      confirm.onclick = () => {
        dialog.close();
        this._dropGifts(giftIds, dropKey);
      };
    }
    if (cancel) {
      cancel.onclick = () => {
        dialog.close();
        if (this.storeSelect) this.storeSelect.focus();
      };
    }

    dialog.showModal();
  }

  /**
   * Hands the gifts to the engine, which removes them from the cart itself.
   * We never delete a gift line directly — the engine would re-add it.
   *
   * `storeKey` is a real store key, or "online" for delivery. The engine files
   * "online" under its own delivery bucket, so both paths work the same way from
   * here. ⚠️ It must be passed explicitly: falling back to currentSelection on
   * the delivery path once sent an undefined key, which the engine rejected —
   * the dialog closed and nothing happened, silently.
   *
   * @param {string[]} giftIds
   * @param {string} [storeKey]
   */
  _dropGifts(giftIds, storeKey) {
    const store = storeKey || this.currentSelection;
    if (!store || !window.giftApp?.suppressGiftsAtStore) return;
    window.giftApp.suppressGiftsAtStore(store, giftIds);
  }

  _clearConflictMessage() {
    if (!this.conflictMessage) return;
    this.conflictMessage.textContent = "";
    this.conflictMessage.hidden = true;
    delete this.conflictMessage.dataset.state;
  }

  /**
   * Sets the data-status attribute on the warning banner for CSS styling.
   * @param {'pending' | 'conflict' | 'resolved'} status
   *   - pending: conflict exists, no selection made
   *   - conflict: selection made, but items need to be removed
   *   - resolved: all items can be fulfilled
   */
  _setBannerStatus(status) {
    if (this.warningBanner) {
      this.warningBanner.dataset.status = status;
    }
  }

  /**
   * Reveals one of the step-2 panels (or neither).
   * @param {'online' | 'pickup' | null} which
   */
  _showPanel(which) {
    if (this.deliveryPanel) this.deliveryPanel.hidden = which !== "online";
    if (this.pickupPanel) this.pickupPanel.hidden = which !== "pickup";
  }

  _showPickupConfirm(storeName) {
    if (!this.pickupConfirmButton) return;
    this.pickupConfirmButton.textContent = `Confirm pickup at ${storeName}`;
    this.pickupConfirmButton.hidden = false;
  }

  _hidePickupConfirm() {
    if (this.pickupConfirmButton) this.pickupConfirmButton.hidden = true;
  }

  /**
   * Returns the banner to its initial state: no method chosen, panels hidden,
   * checkout disabled.
   */
  _resetSelection() {
    if (this.fulfilmentSelect) this.fulfilmentSelect.value = "";
    if (this.storeSelect) this.storeSelect.value = "";
    this._showPanel(null);
    this._hidePickupConfirm();
    this.highlightProducts([]);
    this._disableCheckout();
    this._clearConflictMessage();
    this._setBannerStatus("pending");
    this.currentSelection = null;
    // ⚠️ Deliberately does NOT tell the engine to forget the store.
    //
    // This runs whenever the picker falls back to "Please Select" — including
    // after a cart re-render that could not restore the previous choice. Doing
    // so re-awarded a gift the shopper had just chosen to drop, which then had
    // to be dropped again: two cart writes and two full section re-renders per
    // click, and the shopper back where they started.
    //
    // Forgetting the store is a decision only the shopper makes, by choosing
    // delivery — see _applyOnline(), which does notify.
  }

  /**
   * If every item is in-store-only there is nothing to deliver, so the
   * "Deliver my items" option is hidden and disabled in the step-1 dropdown.
   */
  _configureMethodOptions() {
    if (!this.fulfilmentSelect) return;
    const hasDeliverable = Object.keys(this.data).some(
      (id) => this.data[id].availability !== "In Store Only",
    );
    const onlineOption = this.fulfilmentSelect.querySelector(
      'option[value="online"]',
    );
    if (onlineOption) {
      onlineOption.hidden = !hasDeliverable;
      onlineOption.disabled = !hasDeliverable;
    }
  }

  /**
   * Final call-to-action shared by both CTA buttons. When the selection is
   * fully resolved it sends the shopper to the checkout button; otherwise it
   * scrolls to the first highlighted item that still needs removing.
   */
  _handleCtaClick() {
    const resolved = this.checkoutButton && !this.checkoutButton.disabled;
    if (resolved) {
      this.checkoutButton.scrollIntoView({ behavior: "smooth", block: "center" });
      this.checkoutButton.focus();
      return;
    }
    // Not resolved yet: jump to the first highlighted item to remove
    const firstConflict = document.querySelector(".cart-item--conflict");
    (firstConflict || this.conflictMessage)?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }

  _enableCheckout() {
    if (this.checkoutButton) this.checkoutButton.disabled = false;
  }

  _disableCheckout() {
    if (this.checkoutButton) this.checkoutButton.disabled = true;
  }

  _updateCheckoutState(canCheckout) {
    if (this.checkoutButton) this.checkoutButton.disabled = !canCheckout;
  }
}
